import React, { useMemo } from 'react'
import type { CSSProperties } from 'react'
import { Tooltip } from '@enamelai/design-system'
import { Icon as AiReceptionistIcon } from '@enamelai/design-system/icons/ai_receptionist'
import type { ScheduleCardProps } from './ScheduleCard.types'
import {
  SCHEDULE_CARD_DEMO_PATIENT,
  SCHEDULE_CARD_DEMO_TIME,
  SCHEDULE_CARD_DEMO_TYPE,
  getScheduleCardColorTokens,
} from './ScheduleCard.types'
import './ScheduleCard.css'

const ScheduleCard: React.FC<ScheduleCardProps> = ({
  timeRange = SCHEDULE_CARD_DEMO_TIME,
  patientName = SCHEDULE_CARD_DEMO_PATIENT,
  appointmentType = SCHEDULE_CARD_DEMO_TYPE,
  color = 'pale-green',
  colors,
  headerIcons,
  aiAssisted = false,
  aiHint,
  showTime = true,
  showAppointmentType = true,
  className = '',
  style,
  onClick,
}) => {
  const tokens = useMemo(() => {
    const base = getScheduleCardColorTokens(color)
    return {
      surface: colors?.surface ?? base.surface,
      border: colors?.border ?? base.border,
      header: colors?.header ?? base.header,
      text: colors?.text ?? base.text,
      headerText: colors?.headerText ?? base.headerText,
    }
  }, [color, colors])

  const classNames = [
    'ds-schedule-card',
    `ds-schedule-card--${color}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const cssVars = {
    '--ds-schedule-card-surface': tokens.surface,
    '--ds-schedule-card-border': tokens.border,
    '--ds-schedule-card-header': tokens.header,
    '--ds-schedule-card-text': tokens.text,
    '--ds-schedule-card-header-text': tokens.headerText,
  } as CSSProperties

  const content = (
    <>
      {showTime || headerIcons || aiAssisted ? (
        <div className="ds-schedule-card__header">
          <div className="ds-schedule-card__leading">
            {showTime ? (
              <p className="ds-schedule-card__time">{timeRange}</p>
            ) : null}
            {aiAssisted ? (
              <Tooltip
                content={aiHint || 'Booked by AI receptionist'}
                placement="top"
                portal
              >
                <span className="ds-schedule-card__ai-wrap">
                  <AiReceptionistIcon
                    className="ds-schedule-card__ai-icon"
                    aria-label={aiHint || 'Booked by AI receptionist'}
                  />
                </span>
              </Tooltip>
            ) : null}
          </div>
          {headerIcons ? (
            <div className="ds-schedule-card__icons">{headerIcons}</div>
          ) : null}
        </div>
      ) : null}

      <div className="ds-schedule-card__body">
        <p className="ds-schedule-card__patient">{patientName}</p>
        {showAppointmentType && appointmentType ? (
          <p className="ds-schedule-card__type">{appointmentType}</p>
        ) : null}
      </div>
    </>
  )

  if (onClick) {
    return (
      <button
        type="button"
        className={classNames}
        style={{ ...cssVars, ...style }}
        onClick={onClick}
      >
        {content}
      </button>
    )
  }

  return (
    <div className={classNames} style={{ ...cssVars, ...style }}>
      {content}
    </div>
  )
}

export default ScheduleCard
